import React, { ReactNode, useRef, useState, useEffect, useCallback } from "react";
import { Camera, Scan } from "lucide-react";
import html2canvas from "html2canvas";
import { cn } from "@/lib/utils";
import { AnnotationDialog } from "./AnnotationDialog";

interface AnnotatableCardProps {
  children: ReactNode;
  containerId?: string;
  className?: string;
}

interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export const AnnotatableCard = ({ 
  children, 
  containerId,
  className 
}: AnnotatableCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<Blob | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selection, setSelection] = useState<SelectionBox | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleCapture = async () => {
    if (!containerRef.current || isCapturing) return;
    
    setIsCapturing(true);
    
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true,
      });
      
      canvas.toBlob((blob) => {
        if (blob) {
          setCapturedImage(blob);
          setIsDialogOpen(true);
        }
        setIsCapturing(false);
      }, 'image/png');
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      setIsCapturing(false);
    }
  };

  const handleStartSelection = () => {
    setIsSelecting(true);
    setSelection(null);
  };

  const isInteractiveTarget = (el: EventTarget | null) => {
    if (!el || !(el instanceof HTMLElement)) return false;
    const tag = el.tagName.toLowerCase();
    const role = el.getAttribute?.("role");
    if (tag === "button" || tag === "a" || tag === "input" || tag === "select" || tag === "textarea") return true;
    if (role === "button" || role === "link" || role === "menuitem") return true;
    if (el.closest?.("button, a, [role=button], [role=link], input, select, textarea")) return true;
    return false;
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isSelecting || !containerRef.current) return;
    if (isInteractiveTarget(e.target)) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setSelection({
      startX: x,
      startY: y,
      endX: x,
      endY: y
    });
    setIsDragging(true);
  }, [isSelecting]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !isSelecting || !containerRef.current || !selection) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    
    setSelection(prev => prev ? { ...prev, endX: x, endY: y } : null);
  }, [isDragging, isSelecting, selection]);

  const handleMouseUp = useCallback(async () => {
    if (!isDragging || !selection || !containerRef.current) {
      setIsDragging(false);
      return;
    }
    
    setIsDragging(false);
    
    const width = Math.abs(selection.endX - selection.startX);
    const height = Math.abs(selection.endY - selection.startY);
    
    // Minimum selection size
    if (width < 20 || height < 20) {
      setIsSelecting(false);
      setSelection(null);
      return;
    }
    
    setIsCapturing(true);
    
    try {
      // Capture the entire container first
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true,
      });
      
      // Calculate the crop area (accounting for scale)
      const scale = 2;
      const cropX = Math.min(selection.startX, selection.endX) * scale;
      const cropY = Math.min(selection.startY, selection.endY) * scale;
      const cropWidth = width * scale;
      const cropHeight = height * scale;
      
      // Create a new canvas for the cropped area
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;
      const ctx = croppedCanvas.getContext('2d');
      
      if (ctx) {
        ctx.drawImage(
          canvas,
          cropX, cropY, cropWidth, cropHeight,
          0, 0, cropWidth, cropHeight
        );
        
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            setCapturedImage(blob);
            setIsDialogOpen(true);
          }
          setIsCapturing(false);
          setIsSelecting(false);
          setSelection(null);
        }, 'image/png');
      }
    } catch (error) {
      console.error('Failed to capture selection:', error);
      setIsCapturing(false);
      setIsSelecting(false);
      setSelection(null);
    }
  }, [isDragging, selection]);

  // Cancel selection on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSelecting) {
        setIsSelecting(false);
        setSelection(null);
        setIsDragging(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSelecting]);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setCapturedImage(null);
  };

  // Calculate selection rectangle for display
  const selectionRect = selection ? {
    left: Math.min(selection.startX, selection.endX),
    top: Math.min(selection.startY, selection.endY),
    width: Math.abs(selection.endX - selection.startX),
    height: Math.abs(selection.endY - selection.startY)
  } : null;

  const buttonsClassName = cn(
    "absolute top-0 right-2 z-10 flex gap-1",
    "opacity-0 group-hover:opacity-100",
    "transition-opacity duration-200",
    isSelecting ? "opacity-100" : ""
  );
  const buttonBaseClass = "p-1.5 rounded-md bg-background/80 backdrop-blur-sm border border-border";
  const instructionsClass = "absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-background/90 backdrop-blur-sm rounded-md text-xs text-muted-foreground border border-border";
  const overlayDarkClass = "absolute bg-black/40";

  const cardContent = (
      <div
        ref={containerRef}
        className={cn("relative group overflow-visible pt-4", className)}
        style={{ overflow: "visible" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => isDragging && handleMouseUp()}
      >
        {children}

        {/* Selection overlay */}
        {isSelecting && (
          <div 
            className="absolute inset-0 z-20 cursor-crosshair"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
          >
            {selectionRect && selectionRect.width > 0 && selectionRect.height > 0 && (
              <>
                {/* Darkened areas outside selection */}
                <div 
                  className={overlayDarkClass}
                  style={{ top: 0, left: 0, right: 0, height: selectionRect.top }}
                />
                <div 
                  className={overlayDarkClass}
                  style={{ 
                    top: selectionRect.top, 
                    left: 0, 
                    width: selectionRect.left, 
                    height: selectionRect.height 
                  }}
                />
                <div 
                  className={overlayDarkClass}
                  style={{ 
                    top: selectionRect.top, 
                    right: 0, 
                    left: selectionRect.left + selectionRect.width, 
                    height: selectionRect.height 
                  }}
                />
                <div 
                  className={overlayDarkClass}
                  style={{ 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    top: selectionRect.top + selectionRect.height 
                  }}
                />
                {/* Selection border */}
                <div 
                  className="absolute border-2 border-primary border-dashed"
                  style={{
                    left: selectionRect.left,
                    top: selectionRect.top,
                    width: selectionRect.width,
                    height: selectionRect.height
                  }}
                />
              </>
            )}
            {/* Instructions */}
            <div className={instructionsClass}>
              Drag to select area • ESC to cancel
            </div>
          </div>
        )}
        
        {React.createElement(
          "div",
          { className: buttonsClassName },
          React.createElement(
            "button",
            {
              onClick: handleStartSelection,
              disabled: isCapturing || isSelecting,
              className: cn(
                buttonBaseClass,
                "hover:bg-accent hover:text-accent-foreground",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isSelecting ? "bg-primary text-primary-foreground" : ""
              ),
              title: "Select area to capture",
            },
            React.createElement(Scan, { className: "h-4 w-4" })
          ),
          React.createElement(
            "button",
            {
              onClick: handleCapture,
              disabled: isCapturing || isSelecting,
              className: cn(
                buttonBaseClass,
                "hover:bg-accent hover:text-accent-foreground",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isCapturing ? "animate-pulse" : ""
              ),
              title: "Capture entire card",
            },
            React.createElement(Camera, { className: "h-4 w-4" })
          )
        )}
      </div>
  );

  return (
    <>
      {cardContent}
      <AnnotationDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        capturedImage={capturedImage}
        containerId={containerId}
      />
    </>
  );
};
